import axios from "../../axios";
import { LOCALE_PREFERENCE_KEY } from "../../i18n/localePreferenceKey";
import logger from "../../shared/Logger";
import { buildApiV3Url } from "../../shared/Utils";
import { AppDispatch, RootState } from "../types";
import {
    Preference,
    PreferencesState,
    setError,
    setIsLoadingPreferences,
    setPreferences,
} from "./index";

interface PreferencesResponse {
    value?: Preference[];
}

/** Typed accessor for the preferences slice, since RootState uses an index signature. */
const selectPreferencesState = (state: RootState): PreferencesState =>
    state.preferences as PreferencesState;

/**
 * Fetches all ECM preferences and stores them in Redux
 * Relies on Redux state sharing to prevent duplicate calls
 */
export const fetchAllPreferences =
    (forceRefresh: boolean = false) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<Preference[]> => {
        const { preferences, isLoadingPreferences } = selectPreferencesState(getState());

        // Return existing data if already loaded
        if (preferences && preferences.length > 0 && !forceRefresh) {
            return preferences ?? [];
        }

        // Prevent multiple simultaneous requests
        if (isLoadingPreferences) {
            return preferences ?? [];
        }

        dispatch(setIsLoadingPreferences(true));

        try {
            const response = await axios.get<PreferencesResponse>(
                buildApiV3Url("ecm-preferences"),
                {
                    withCredentials: true,
                }
            );

            const preferencesData: Preference[] = response?.data?.value || [];
            dispatch(setPreferences(preferencesData));
            dispatch(setIsLoadingPreferences(false));
            return preferencesData;
        } catch {
            const errorMessage = "Failed to fetch preferences";
            logger(2, "error", {
                message: "Error fetching ECM preferences:",
            });
            dispatch(setError(errorMessage));
            dispatch(setIsLoadingPreferences(false));
            return [];
        }
    };

/**
 * Gets a specific preference by name from Redux store
 */
export const getPreference =
    (preferenceName: string) =>
    (dispatch: AppDispatch, getState: () => RootState): Preference | null => {
        const { preferences } = selectPreferencesState(getState());
        return preferences?.find(pref => pref.preference === preferenceName) ?? null;
    };

/**
 * Gets a parsed preference value by name from Redux store
 */
export const getPreferenceValue =
    (preferenceName: string) =>
    (dispatch: AppDispatch): unknown => {
        const preference = dispatch(getPreference(preferenceName)) as Preference | null;
        if (!preference?.preferenceValue) {
            return null;
        }

        try {
            return JSON.parse(preference.preferenceValue);
        } catch {
            logger(2, "error", {
                message: `Error parsing preference value for ${preferenceName}:`,
            });
            return null;
        }
    };

/**
 * Gets multiple preferences in a single call from Redux store
 */
export const getMultiplePreferences =
    (preferenceNames: string[]) =>
    (dispatch: AppDispatch, getState: () => RootState): Record<string, unknown> => {
        const { preferences } = selectPreferencesState(getState());
        const result: Record<string, unknown> = {};

        preferenceNames.forEach(name => {
            const preference = preferences?.find(pref => pref.preference === name) ?? null;
            if (preference?.preferenceValue) {
                try {
                    result[name] = JSON.parse(preference.preferenceValue);
                } catch {
                    logger(2, "error", {
                        message: `Error parsing preference value for ${name}:`,
                    });
                    result[name] = null;
                }
            } else {
                result[name] = null;
            }
        });

        return result;
    };

/**
 * Saves the user's selected locale to the ECM preferences API and updates Redux state.
 * Uses the preference key defined in `LOCALE_PREFERENCE_KEY` ("locale").
 *
 * @param locale - BCP 47 locale code to persist (e.g. "en-US")
 */
export const saveLocalePreference =
    (locale: string) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
        try {
            // preference_value is a PostgreSQL json column — the value must be valid JSON.
            // A bare locale string (e.g. "fr-CA") is not valid JSON; wrap it with JSON.stringify.
            const encodedValue = JSON.stringify(locale);
            await axios.post(
                buildApiV3Url("ecm-preferences"),
                { preference: LOCALE_PREFERENCE_KEY, preferenceValue: encodedValue },
                { withCredentials: true }
            );

            // Update the Redux store optimistically so the UI reacts immediately
            const { preferences } = selectPreferencesState(getState());
            const existing: Preference[] = preferences ?? [];
            const updated: Preference[] = existing.map(p =>
                p.preference === LOCALE_PREFERENCE_KEY ? { ...p, preferenceValue: locale } : p
            );

            // If no existing locale preference was found, append the new entry
            const hasExisting = existing.some(p => p.preference === LOCALE_PREFERENCE_KEY);
            if (!hasExisting) {
                updated.push({ preference: LOCALE_PREFERENCE_KEY, preferenceValue: locale });
            }

            dispatch(setPreferences(updated));
        } catch {
            logger(2, "error", {
                message: "Error saving locale preference:",
            });
        }
    };

/**
 * Ensures preferences are loaded before getting specific preferences
 */
export const ensurePreferencesLoaded =
    () =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<Preference[]> => {
        const { preferences, isLoadingPreferences } = selectPreferencesState(getState());

        // If we have data, return it
        if (preferences && preferences.length > 0) {
            return preferences ?? [];
        }

        // If already loading, return the current preferences (they might be empty but loading is in progress)
        if (isLoadingPreferences) {
            return preferences ?? [];
        }

        // Otherwise, fetch data
        return await dispatch(fetchAllPreferences());
    };
